import { Building2, Loader2, Search, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { type Account, type AccountContact, accountApi } from '@/api/commercial/accounts';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import { isApiError } from '@/lib/api';

interface GroupAccount {
  id: string;
  name?: string;
  social_name?: string;
  document: { value: string; type: string };
}

interface SearchContactDialogProps {
  open: boolean;
  onClose: () => void;
  economicGroupId: string;
  currentAccountId: string;
  existingContacts?: { name: string; contactItems?: { contact: string }[] }[];
  onImport: (contacts: Omit<AccountContact, 'id'>[]) => void | Promise<void>;
}

const formatDocument = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

export function SearchContactDialog({
  open,
  onClose,
  economicGroupId,
  currentAccountId,
  existingContacts,
  onImport,
}: SearchContactDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [groupContacts, setGroupContacts] = useState<
    Array<{ account: GroupAccount; contacts: AccountContact[] }>
  >([]);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());

  const contactKey = (name: string, items: { contact: string }[]) =>
    `${name.trim().toLowerCase()}::${[...items.map((i) => i.contact)].sort().join(',')}`;

  const existingContactKeys = new Set(
    (existingContacts || []).map((c) => contactKey(c.name, c.contactItems || [])),
  );
  const isAlreadyImported = (contact: AccountContact) =>
    existingContactKeys.has(contactKey(contact.name, contact.contactItems || []));

  const loadGroupContacts = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const groupAccounts: Account[] = [];
      let page = 1;
      let pages = 1;
      do {
        const result = await accountApi.findMany({ economicGroupId, page, limit: 100 });
        groupAccounts.push(
          ...result.docs.filter(
            (account) =>
              account.economicGroupId === economicGroupId && account.id !== currentAccountId,
          ),
        );
        pages = result.pages;
        page += 1;
      } while (page <= pages);

      // Fetch contacts for each account
      const contactsByAccount = await Promise.all(
        groupAccounts.map(async (account) => {
          const contacts: AccountContact[] = [];
          let contactPage = 1;
          let pageContacts: AccountContact[];
          do {
            pageContacts = await accountApi.findContacts(account.id, contactPage, 100);
            contacts.push(...pageContacts);
            contactPage += 1;
          } while (pageContacts.length === 100);
          return { account: account as GroupAccount, contacts };
        }),
      );

      setGroupContacts(contactsByAccount);
    } catch {
      setGroupContacts([]);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [economicGroupId, currentAccountId]);

  useEffect(() => {
    if (open) {
      setSelectedContactIds(new Set());
      setSaveError(null);
      loadGroupContacts();
    }
  }, [open, loadGroupContacts]);

  const toggleContact = (contactId: string) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const handleImport = async () => {
    setIsSaving(true);
    setSaveError(null);
    const selectedContacts: Omit<AccountContact, 'id'>[] = [];

    for (const group of groupContacts) {
      for (const contact of group.contacts) {
        if (selectedContactIds.has(contact.id) && !isAlreadyImported(contact)) {
          selectedContacts.push({
            accountId: currentAccountId,
            name: contact.name,
            contractEnabled: contact.contractEnabled ?? false,
            positionOrRelation: contact.positionOrRelation || '',
            area: contact.area || undefined,
            department: contact.department || undefined,
            cpf: contact.cpf || undefined,
            rg: contact.rg || undefined,
            taxId: contact.taxId || undefined,
            contactFor: contact.contactFor || [],
            contactItems: (contact.contactItems || []).map((item) => ({
              ...item,
              preferredContact: item.preferredContact || {},
            })),
          });
        }
      }
    }

    try {
      await onImport(selectedContacts);
      onClose();
    } catch (error) {
      setSaveError(
        isApiError(error) && error.response?.data.message
          ? error.response.data.message
          : 'Não foi possível salvar todos os contatos selecionados. Tente novamente.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSaving && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Busca de Contatos
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Buscando contatos do grupo...
            </span>
          </div>
        ) : loadError ? (
          <div className="space-y-3 py-6 text-center" role="alert">
            <p className="text-sm text-destructive">
              Não foi possível buscar os contatos do grupo.
            </p>
            <Button type="button" variant="outline" onClick={loadGroupContacts}>
              Tentar novamente
            </Button>
          </div>
        ) : groupContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <UserPlus className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              Nenhuma outra empresa cadastrada neste grupo econômico.
            </p>
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto">
            <Accordion type="multiple">
              {groupContacts.map((group) => (
                <AccordionItem key={group.account.id} value={group.account.id}>
                  <AccordionTrigger className="gap-2">
                    <span className="flex min-w-0 flex-wrap items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary" />
                      <span className="font-medium text-sm">
                        {group.account.social_name || group.account.name}
                      </span>
                      <span className="text-xs text-muted-foreground font-mono">
                        {formatDocument(group.account.document.value)}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {group.contacts.length}
                      </Badge>
                    </span>
                  </AccordionTrigger>

                  <AccordionContent className="space-y-1.5">
                    {group.contacts.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        Nenhum contato cadastrado nesta empresa.
                      </p>
                    )}
                    {group.contacts.map((contact) => {
                      const alreadyExists = isAlreadyImported(contact);
                      return (
                        <label
                          key={contact.id}
                          htmlFor={`contact-${contact.id}`}
                          className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                            alreadyExists
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-accent/50 cursor-pointer'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{contact.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {[
                                contact.positionOrRelation,
                                contact.contactItems
                                  ?.map((i) =>
                                    i.type === 'Email' ? i.contact : formatPhone(i.contact),
                                  )
                                  .join(', '),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                            {alreadyExists && (
                              <p className="text-xs text-amber-600 mt-0.5">Contato já importado</p>
                            )}
                          </div>
                          <Checkbox
                            id={`contact-${contact.id}`}
                            checked={selectedContactIds.has(contact.id)}
                            onCheckedChange={() => !alreadyExists && toggleContact(contact.id)}
                            disabled={alreadyExists || isSaving}
                          />
                        </label>
                      );
                    })}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        <FormError error={saveError} onDismiss={() => setSaveError(null)} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={isLoading || loadError || isSaving || selectedContactIds.size === 0}
          >
            <UserPlus className="h-4 w-4 mr-1" />
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
